import { useCallback, useEffect, useRef, useState } from 'react'
import {
  CheckCircle2,
  FileText,
  RefreshCw,
  Upload as UploadIcon,
  X,
  AlertCircle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { AlertBanner } from '../components/ui/AlertBanner'
import { LimitExceededBanner } from '../components/ui/LimitExceededBanner'
import { LimitExceededError } from '../lib/api'
import { useJobStatus, useJobStatusWS, useUploadFile, type JobStatus } from '../hooks/useIngestion'
import { MappingEditor } from '../components/connectors/MappingEditor'
import {
  usePreviewMapping,
  useConfirmMapping,
  useNamedMappings,
  useApplyNamedMapping,
  usePlatformMappingTemplates,
  useApplyPlatformTemplate,
} from '../hooks/useMapping'
import { DataCoverageChecklist } from '../components/upload/DataCoverageChecklist'
import { useTenant } from '../hooks/useTenant'
import { useBusinessUnits } from '../hooks/useBusinessUnits'

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_FILE_SIZE_MB = 50
const ACCEPTED_TYPES: Record<string, string> = {
  'text/csv': 'CSV',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/json': 'JSON',
  'application/pdf': 'PDF',
  'text/xml': 'XML',
  'application/xml': 'XML',
}
const ACCEPTED_EXTENSIONS = ['.csv', '.xls', '.xlsx', '.json', '.pdf', '.xml']

const STEPS: { status: string; label: string }[] = [
  { status: 'pending', label: 'File ricevuto' },
  { status: 'parsing', label: 'Analisi file' },
  { status: 'mapping', label: 'Inferenza schema' },
  { status: 'validating', label: 'Validazione dati' },
  { status: 'storing', label: 'Salvataggio' },
  { status: 'kpi_computing', label: 'Calcolo KPI' },
  { status: 'completed', label: 'Completato' },
]

const STATUS_ORDER = STEPS.map((s) => s.status)

const INDUSTRY_OPTIONS: { value: string; label: string }[] = [
  { value: 'servizi_professionali', label: 'Servizi professionali (consulenza, legale, accounting)' },
  { value: 'saas_msp', label: 'SaaS / MSP (software, managed services, cloud)' },
  { value: 'manifatturiero', label: 'Manifatturiero (produzione, lavorazioni)' },
  { value: 'logistica_trasporti', label: 'Logistica e trasporti' },
  { value: 'field_service', label: 'Field Service (manutenzione, assistenza tecnica)' },
  { value: 'agenzie_marketing', label: 'Agenzie marketing e comunicazione' },
  { value: 'distribuzione_wholesale', label: 'Distribuzione e wholesale' },
  { value: 'system_integrator', label: 'System Integrator (IT, infrastruttura)' },
]

const STATUS_PROGRESS: Record<string, number> = {
  pending: 10,
  parsing: 25,
  mapping: 45,
  validating: 60,
  storing: 75,
  kpi_computing: 88,
  completed: 100,
  failed: 0,
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function validateFile(file: File): string | null {
  const sizeMB = file.size / (1024 * 1024)
  if (sizeMB > MAX_FILE_SIZE_MB) {
    return `Il file supera i ${MAX_FILE_SIZE_MB} MB. Suddividerlo in parti più piccole.`
  }

  const ext = '.' + file.name.split('.').pop()?.toLowerCase()
  const typeOk = file.type in ACCEPTED_TYPES
  const extOk = ACCEPTED_EXTENSIONS.includes(ext)

  if (!typeOk && !extOk) {
    return `Formato file non supportato. Formati accettati: CSV, Excel, JSON, PDF, XML.`
  }

  return null
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function StepIndicator({ currentStatus }: { currentStatus: string }) {
  const currentIdx = STATUS_ORDER.indexOf(currentStatus)
  const isFailed = currentStatus === 'failed'

  return (
    <ol className="flex flex-col gap-2" aria-label="Avanzamento elaborazione">
      {STEPS.map((step, idx) => {
        const done = !isFailed && currentIdx > idx
        const active = !isFailed && currentIdx === idx
        const pending = isFailed ? true : currentIdx < idx

        return (
          <li key={step.status} className="flex items-center gap-3">
            <span
              className={clsx(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                done && 'bg-emerald-500/20 text-emerald-400',
                active && 'bg-[--km-primary]/20 text-[--km-primary] ring-1 ring-[--km-primary]/40',
                pending && 'bg-[--km-surface-3] text-[--km-text-muted]',
              )}
              aria-hidden="true"
            >
              {done ? <CheckCircle2 size={14} /> : idx + 1}
            </span>
            <span
              className={clsx(
                'text-sm',
                done && 'text-emerald-400',
                active && 'text-[--km-text-primary] font-medium',
                pending && 'text-[--km-text-muted]',
              )}
            >
              {step.label}
            </span>
            {active && (
              <span className="ml-auto">
                <RefreshCw size={12} className="animate-spin text-[--km-primary]" aria-label="In corso" />
              </span>
            )}
          </li>
        )
      })}
    </ol>
  )
}

function ProgressBar({ pct, failed }: { pct: number; failed: boolean }) {
  return (
    <div className="h-1.5 w-full rounded-full bg-[--km-surface-3] overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
      <div
        className={clsx(
          'h-full rounded-full transition-all duration-700',
          failed ? 'bg-[--km-error]' : 'bg-[--km-primary]',
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

function ErrorsPreview({ errors }: { errors: Array<Record<string, unknown>> }) {
  if (errors.length === 0) return null

  return (
    <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4 mt-4">
      <p className="text-xs font-semibold uppercase tracking-widest text-[--km-error] mb-3">
        Errori rilevati (anteprima)
      </p>
      <ul className="space-y-1.5">
        {errors.slice(0, 5).map((err, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[--km-text-secondary]">
            <AlertCircle size={13} className="mt-0.5 shrink-0 text-[--km-error]" />
            <span>{String(err.message ?? err.error ?? JSON.stringify(err))}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

type Phase = 'idle' | 'uploading' | 'processing' | 'completed' | 'failed'

export function Upload() {
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>('idle')
  const [selectedBuId, setSelectedBuId] = useState<string>('')
  const [selectedIndustry, setSelectedIndustry] = useState<string>('')

  const queryClient = useQueryClient()
  const uploadMutation = useUploadFile()
  const { data: tenant } = useTenant()

  // Pre-seleziona il settore dalle impostazioni tenant (OB.1)
  useEffect(() => {
    if (tenant?.settings?.industry && !selectedIndustry) {
      setSelectedIndustry(tenant.settings.industry as string)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.settings?.industry])
  const { data: buList } = useBusinessUnits()
  const isEnterprise = tenant?.plan === 'enterprise'
  const activeBUs = buList?.filter((bu) => bu.is_active) ?? []
  const showBuDropdown = isEnterprise && activeBUs.length > 0
  const wsData = useJobStatusWS(jobId)
  const { data: pollData } = useJobStatus(jobId)

  // Prefer WS data when available, fall back to REST polling
  const jobStatus: JobStatus | null = wsData ?? pollData ?? null

  const needsMapping = jobStatus?.status === 'mapping'
  const { data: mappingProposal } = usePreviewMapping(needsMapping ? jobId : null)
  const confirmMappingMutation = useConfirmMapping(jobId ?? '')
  const { data: namedTemplates } = useNamedMappings()
  const applyNamedMappingMutation = useApplyNamedMapping()
  const { data: platformTemplates } = usePlatformMappingTemplates()
  const applyPlatformTemplateMutation = useApplyPlatformTemplate()

  // Derive phase from jobStatus
  const effectivePhase: Phase = (() => {
    if (phase === 'idle' || phase === 'uploading') return phase
    if (phase === 'failed') return 'failed'
    if (!jobStatus) return 'processing'
    if (jobStatus.status === 'completed') return 'completed'
    if (jobStatus.status === 'failed') return 'failed'
    return 'processing'
  })()

  const progress = jobStatus
    ? (jobStatus.progress_pct > 0 ? jobStatus.progress_pct : STATUS_PROGRESS[jobStatus.status] ?? 10)
    : effectivePhase === 'uploading' ? 5 : 0

  // ── Event handlers ──────────────────────────────────────────────────────

  const handleFileChange = useCallback((candidate: File) => {
    const err = validateFile(candidate)
    if (err) {
      setFileError(err)
      setFile(null)
    } else {
      setFileError(null)
      setFile(candidate)
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) handleFileChange(f)
    // reset input so same file can be re-selected
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) handleFileChange(f)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => setDragOver(false)

  const handleUpload = async () => {
    if (!file) return
    setPhase('uploading')
    try {
      const res = await uploadMutation.mutateAsync({
        file,
        defaultBuId: selectedBuId || null,
        industry: selectedIndustry || null,
      })
      setJobId(res.job_id)
      setPhase('processing')
    } catch (err) {
      setPhase('failed')
    }
  }

  const handleReset = () => {
    setFile(null)
    setFileError(null)
    setJobId(null)
    setPhase('idle')
    setSelectedBuId('')
    setSelectedIndustry('')
    uploadMutation.reset()
  }

  // ── Render: success ──────────────────────────────────────────────────────

  if (effectivePhase === 'completed' && jobStatus) {
    return (
      <section aria-label="Upload completato" className="space-y-6">
        <h1 className="text-2xl font-semibold text-[--km-text-primary]">Carica file</h1>

        <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
          <CheckCircle2 size={48} className="mx-auto mb-4 text-emerald-400" aria-hidden="true" />
          <p className="text-lg font-semibold text-[--km-text-primary] mb-1">
            File elaborato con successo
          </p>
          <p className="text-sm text-[--km-text-muted] mb-6">{jobStatus.message_it}</p>

          {/* Rows stats */}
          {jobStatus.rows_total != null && (
            <div className="flex flex-wrap justify-center gap-6 mb-6" role="region" aria-label="Statistiche righe">
              <div className="text-center">
                <p className="text-2xl font-semibold tabular-nums text-[--km-text-primary]">
                  {jobStatus.rows_total.toLocaleString('it-IT')}
                </p>
                <p className="text-xs text-[--km-text-muted] mt-0.5">Righe totali</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-semibold tabular-nums text-emerald-400">
                  {(jobStatus.rows_valid ?? 0).toLocaleString('it-IT')}
                </p>
                <p className="text-xs text-[--km-text-muted] mt-0.5">Importate</p>
              </div>
              {(jobStatus.rows_error ?? 0) > 0 && (
                <div className="text-center">
                  <p className="text-2xl font-semibold tabular-nums text-[--km-warning]">
                    {(jobStatus.rows_error ?? 0).toLocaleString('it-IT')}
                  </p>
                  <p className="text-xs text-[--km-text-muted] mt-0.5">Errori</p>
                </div>
              )}
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="rounded-lg bg-[--km-primary] px-5 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
            >
              Vai alla dashboard
            </Link>
            <button
              onClick={handleReset}
              className="rounded-lg border border-[--km-border] px-5 py-2 text-sm font-medium text-[--km-text-secondary] transition-colors hover:border-[--km-border-hover] hover:text-[--km-text-primary]"
            >
              Carica un altro file
            </button>
          </div>
        </div>
      </section>
    )
  }

  // ── Render: failed ───────────────────────────────────────────────────────

  if (effectivePhase === 'failed') {
    const errMsg = jobStatus?.message_it ?? uploadMutation.error?.message ?? 'Errore durante l\'elaborazione del file.'

    return (
      <section aria-label="Upload fallito" className="space-y-6">
        <h1 className="text-2xl font-semibold text-[--km-text-primary]">Carica file</h1>

        <LimitExceededBanner error={uploadMutation.error} />
        {!(uploadMutation.error instanceof LimitExceededError) && (
          <AlertBanner severity="critical" message={errMsg} />
        )}

        {jobStatus?.errors_preview && jobStatus.errors_preview.length > 0 && (
          <ErrorsPreview errors={jobStatus.errors_preview} />
        )}

        <button
          onClick={handleReset}
          className="flex items-center gap-2 rounded-lg border border-[--km-border] px-4 py-2 text-sm font-medium text-[--km-text-secondary] transition-colors hover:border-[--km-border-hover] hover:text-[--km-text-primary]"
        >
          <RefreshCw size={14} />
          Riprova
        </button>
      </section>
    )
  }

  // ── Render: processing ───────────────────────────────────────────────────

  if (effectivePhase === 'processing' || effectivePhase === 'uploading') {
    const isFailed = false
    const currentStatus = effectivePhase === 'uploading' ? 'pending' : (jobStatus?.status ?? 'pending')

    return (
      <section aria-label="Elaborazione in corso" className="space-y-6">
        <h1 className="text-2xl font-semibold text-[--km-text-primary]">Carica file</h1>

        <div className="rounded-xl border border-[--km-border] bg-[--km-surface] p-6">
          {/* File info */}
          {file && (
            <div className="flex items-center gap-3 mb-6 p-3 rounded-lg bg-[--km-surface-2] border border-[--km-border]">
              <FileText size={18} className="shrink-0 text-[--km-primary]" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-[--km-text-primary] truncate">{file.name}</p>
                <p className="text-xs text-[--km-text-muted]">{formatBytes(file.size)}</p>
              </div>
            </div>
          )}

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[--km-text-secondary]">
                {jobStatus?.message_it ?? 'Caricamento in corso…'}
              </p>
              <span className="text-xs tabular-nums text-[--km-text-muted]">{progress}%</span>
            </div>
            <ProgressBar pct={progress} failed={isFailed} />
          </div>

          {/* Step indicators */}
          <StepIndicator currentStatus={currentStatus} />
        </div>

        {/* Template certificati Koremargin (US-1.4) — un click per formato gestionale */}
        {needsMapping && platformTemplates && platformTemplates.length > 0 && jobId && (
          <div className="mb-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-4 py-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs font-semibold text-emerald-400">
                ✓ Certificato Koremargin
              </span>
              <span className="text-xs text-[--km-text-secondary]">Template ufficiali per i principali gestionali</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {platformTemplates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  disabled={applyPlatformTemplateMutation.isPending}
                  onClick={async () => {
                    await applyPlatformTemplateMutation.mutateAsync({ templateId: t.id, jobId })
                    queryClient.invalidateQueries({ queryKey: ['job', jobId] })
                  }}
                  title={t.description ?? undefined}
                  className="flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-[--km-surface] px-3 py-1.5 text-xs font-medium text-[--km-text-primary] hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-colors disabled:opacity-50"
                >
                  {t.template_source && (
                    <span className="text-emerald-400 font-semibold">{t.template_source}</span>
                  )}
                  {t.name}
                  <span className="text-[--km-text-muted]">({t.columns_count} col.)</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Dropdown template nominati — visibile quando job attende mapping e ci sono template */}
        {needsMapping && namedTemplates && namedTemplates.length > 0 && jobId && (
          <div className="mb-4 flex items-center gap-3 rounded-lg border border-[--km-border] bg-[--km-surface] px-4 py-3">
            <span className="text-sm text-[--km-text-secondary] whitespace-nowrap">I miei template:</span>
            <select
              defaultValue=""
              onChange={async (e) => {
                const templateId = e.target.value
                if (!templateId) return
                await applyNamedMappingMutation.mutateAsync({ templateId, jobId })
                queryClient.invalidateQueries({ queryKey: ['job', jobId] })
              }}
              disabled={applyNamedMappingMutation.isPending}
              className="flex-1 rounded-lg border border-[--km-border] bg-[--km-surface] px-3 py-1.5 text-sm text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]"
            >
              <option value="">— Scegli un template salvato —</option>
              {namedTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.columns_count} colonne)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* MappingEditor — mostrato solo quando il job attende conferma colonne */}
        {needsMapping && mappingProposal && (
          <MappingEditor
            proposal={mappingProposal}
            isSubmitting={confirmMappingMutation.isPending}
            dataSourceId={jobStatus?.data_source_id}
            onCancel={handleReset}
            onConfirm={async (mapping) => {
              await confirmMappingMutation.mutateAsync({ mapping })
              // Invalida il polling per rileggere il nuovo status (validating)
              queryClient.invalidateQueries({ queryKey: ['job', jobId] })
            }}
          />
        )}
      </section>
    )
  }

  // ── Render: idle (drop zone) ─────────────────────────────────────────────

  return (
    <section aria-label="Carica file" className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-[--km-text-primary]">Carica file</h1>
        <p className="text-sm text-[--km-text-muted] mt-0.5">
          Importa CSV, Excel, JSON, PDF o XML. Il motore di inferenza mappa automaticamente le
          colonne al canonical schema.
        </p>
      </div>

      {/* Drop zone */}
      <div
        role="region"
        aria-label="Zona di trascinamento file"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
        className={clsx(
          'flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed p-8 sm:p-12 cursor-pointer',
          'transition-colors duration-150',
          dragOver
            ? 'border-[--km-primary] bg-[--km-primary]/5'
            : 'border-[--km-border] bg-[--km-surface] hover:border-[--km-border-hover]',
        )}
      >
        <div
          className={clsx(
            'flex h-14 w-14 items-center justify-center rounded-full',
            dragOver ? 'bg-[--km-primary]/20 text-[--km-primary]' : 'bg-[--km-surface-3] text-[--km-text-muted]',
          )}
          aria-hidden="true"
        >
          <UploadIcon size={24} />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-[--km-text-primary]">
            Trascina il file qui oppure{' '}
            <span className="text-[--km-primary] underline underline-offset-2">sfoglia</span>
          </p>
          <p className="text-xs text-[--km-text-muted] mt-1">
            CSV, Excel, JSON, PDF, XML — max {MAX_FILE_SIZE_MB} MB
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={handleInputChange}
          className="sr-only"
          aria-label="Seleziona file da caricare"
          data-testid="file-input"
        />
      </div>

      {/* Validation error */}
      {fileError && <AlertBanner severity="critical" message={fileError} onDismiss={() => setFileError(null)} />}

      {/* Selected file preview + upload button */}
      {file && !fileError && (
        <div className="flex items-center gap-4 rounded-xl border border-[--km-border] bg-[--km-surface] p-4">
          <FileText size={20} className="shrink-0 text-[--km-primary]" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[--km-text-primary] truncate" title={file.name}>
              {file.name}
            </p>
            <p className="text-xs text-[--km-text-muted]">
              {formatBytes(file.size)} · {ACCEPTED_TYPES[file.type] ?? file.name.split('.').pop()?.toUpperCase()}
            </p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation()
              setFile(null)
              setFileError(null)
            }}
            className="shrink-0 p-1.5 rounded-md text-[--km-text-muted] hover:text-[--km-text-primary] transition-colors"
            aria-label="Rimuovi file selezionato"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Industry dropdown — migliora l'inferenza automatica del mapping */}
      {file && !fileError && (
        <div className="rounded-xl border border-[--km-border] bg-[--km-surface] px-4 py-3 flex items-center gap-3">
          <label htmlFor="industry-select" className="text-sm text-[--km-text-secondary] whitespace-nowrap">
            Settore:
          </label>
          <select
            id="industry-select"
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
            className="flex-1 rounded-lg border border-[--km-border] bg-[--km-surface] px-3 py-1.5 text-sm text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]"
          >
            <option value="">— Generico (nessun settore specifico) —</option>
            {INDUSTRY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* BU dropdown — solo Enterprise con BU configurate */}
      {file && !fileError && showBuDropdown && (
        <div className="rounded-xl border border-[--km-border] bg-[--km-surface] px-4 py-3 flex items-center gap-3">
          <label htmlFor="default-bu-select" className="text-sm text-[--km-text-secondary] whitespace-nowrap">
            Business Unit predefinita:
          </label>
          <select
            id="default-bu-select"
            value={selectedBuId}
            onChange={(e) => setSelectedBuId(e.target.value)}
            className="flex-1 rounded-lg border border-[--km-border] bg-[--km-surface] px-3 py-1.5 text-sm text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]"
          >
            <option value="">— Nessuna (rilevata dal file) —</option>
            {activeBUs.map((bu) => (
              <option key={bu.id} value={bu.id}>
                {bu.name} ({bu.code})
              </option>
            ))}
          </select>
        </div>
      )}

      {file && !fileError && (
        <div className="flex justify-end">
          <button
            onClick={handleUpload}
            disabled={uploadMutation.isPending}
            className={clsx(
              'w-full sm:w-auto flex items-center justify-center gap-2 rounded-lg bg-[--km-primary] px-6 py-2.5 text-sm font-medium text-white',
              'transition-opacity',
              uploadMutation.isPending ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90',
            )}
          >
            {uploadMutation.isPending ? (
              <RefreshCw size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <UploadIcon size={14} aria-hidden="true" />
            )}
            {uploadMutation.isPending ? 'Caricamento…' : 'Carica file'}
          </button>
        </div>
      )}

      {/* Info box */}
      <div className="rounded-xl border border-[--km-border] bg-[--km-surface] p-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-[--km-text-secondary] mb-3">
          Formati supportati
        </p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
          {[
            { ext: 'CSV', desc: 'Dati tabellari separati da virgola o punto e virgola' },
            { ext: 'Excel (.xlsx/.xls)', desc: 'Fogli di calcolo Microsoft Excel' },
            { ext: 'JSON', desc: 'Dati strutturati in formato JSON' },
            { ext: 'PDF', desc: 'Fatture PA e documenti tabulari' },
            { ext: 'XML', desc: 'Export da ERP e gestionali' },
          ].map((f) => (
            <div key={f.ext} className="flex items-start gap-2">
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[--km-primary]" aria-hidden="true" />
              <div>
                <p className="text-xs font-medium text-[--km-text-primary]">{f.ext}</p>
                <p className="text-xs text-[--km-text-muted]">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Checklist completezza dati per sezione */}
      <DataCoverageChecklist />
    </section>
  )
}
